<template>
  <div ref="monacoContainer" class="monaco-editor-container"></div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import * as monaco from 'monaco-editor';

// Props
const props = defineProps({
  original: String,
  modified: String,
});

// Refs
const monacoContainer = ref(null);
let editor = null;

// Mount Monaco Editor
onMounted(() => {
  const options = {
    theme: 'myCustomTheme',
    automaticLayout: true,
    renderSideBySide: true,
    language: 'javascript',
    useInlineViewWhenSpaceIsLimited: false,
    renderOverviewRuler: false,
    readOnly: true,
    folding: false,
    lineNumbersMinChars: 2,
    minimap: {
      enabled: false,
    },
    scrollBeyondLastLine: false,
    scrollbar: {
      alwaysConsumeMouseWheel: false,
    },
  };

  monaco.editor.defineTheme('myCustomTheme', {
    base: 'vs', // you can also use 'vs-dark' or 'hc-black'
    inherit: true, // inherit the base theme
    rules: [],
    colors: {
      'diffEditor.removedTextBackground': '#a0a00050',
      'diffEditor.insertedTextBackground': '#00a0a050',
    },
    'workbench.colorCustomizations': {
      'diffEditor.removedTextBackground': '#a0a00050',
      'diffEditor.insertedTextBackground': '#00a0a050',
    },
  });

  if (props.original && props.modified) {
    editor = monaco.editor.createDiffEditor(monacoContainer.value, options);
    monaco.editor.setTheme('myCustomTheme');
    const originalModel = monaco.editor.createModel(props.original, 'javascript');
    const modifiedModel = monaco.editor.createModel(props.modified, 'javascript');

    editor.setModel({
      original: originalModel,
      modified: modifiedModel,
    });

    // Set options for the original editor
    const originalEditor = editor.getOriginalEditor();
    originalEditor.updateOptions({
      scrollbar: {
        alwaysConsumeMouseWheel: false,
      },
    });

    // Set options for the modified editor
    const modifiedEditor = editor.getModifiedEditor();
    modifiedEditor.updateOptions({
      scrollbar: {
        alwaysConsumeMouseWheel: false,
      },
    });

    const lineCountOriginal = editor.getModel().original.getLineCount();
    const lineCountModified = editor.getModel().modified.getLineCount();
    const lineCount = Math.max(lineCountOriginal, lineCountModified);
    monacoContainer.value.style.height = `${Math.min(lineCount, 10) * 19 + 15}px`;
  } else {
    editor = monaco.editor.create(monacoContainer.value, { ...options, value: props.original });
    const lineCount = editor.getModel().getLineCount();
    monacoContainer.value.style.height = `${Math.min(lineCount, 10) * 19 + 15}px`;
  }
});
</script>

<style lang="scss">
@import '@styles/variables.scss';

.monaco-editor-container {
  width: 100%;
  border: 1px solid $border-color;
}
</style>
